import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { TaskEmployeeSelect } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/TaskEmployeeSelect';
import {
  UseEditWorklistTaskDialogProps,
  useEditWorklistTaskDialog,
} from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/useEditWorklistTaskDialog';
import { useSxStyles } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/useStyles';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

type PropsType = {
  open: boolean;
} & UseEditWorklistTaskDialogProps;

export const EditWorklistTaskDialog = (props: PropsType) => {
  const { open, ...dataProps } = props;

  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const {
    form: { control },
    disabledFields,
    onCloseDialog,
    onSubmit,
    taskOptions,
  } = useEditWorklistTaskDialog(dataProps);

  return (
    <DraggableDialog open={open} onClose={onCloseDialog}>
      <SolaForceDialogTitle onClose={onCloseDialog}>
        {t('laEdit')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        <Box sx={sxStyles.root}>
          <Controller
            control={control}
            name={'title'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  value={value}
                  onChange={onChange}
                  label={t('laSubject')}
                  disabled={disabledFields.title}
                  size={'small'}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                  required={true}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'description'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  value={value}
                  onChange={onChange}
                  label={t('laDescription')}
                  disabled={disabledFields.description}
                  size={'small'}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                  multiline={true}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'link'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TextField
                  value={value}
                  onChange={onChange}
                  label={t('url')}
                  disabled={disabledFields.link}
                  size={'small'}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'taskType'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <EnumSelect
                  label={t('laType')}
                  value={value}
                  onChange={onChange}
                  enumOptions={taskOptions.type}
                  size={'small'}
                  fullWidth={true}
                  disabled={disabledFields.taskType}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'dueDate'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <DatePicker
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                  })}
                  label={t('dueDate')}
                  slotProps={{
                    field: {
                      clearable: true,
                    },
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'targetEmployeeId'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TaskEmployeeSelect
                  label={t('employee')}
                  value={value}
                  onChange={onChange}
                  disabled={disabledFields.targetEmployeeId}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'responsibleEmployeeId'}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <TaskEmployeeSelect
                  label={t('responsible')}
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                />
              );
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseDialog}>{t('cancel')}</Button>
        <Button onClick={onSubmit}>{t('save')}</Button>
      </DialogActions>
    </DraggableDialog>
  );
};
